import { Box, Menu, MenuItem } from '@mui/material';
import type { PlanNode } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import { ContractChip } from './ContractChip';
import { useContractSelection } from './useContractSelection';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * The side panel's contract control: which contract a task bills through (v8,
 * Area 5 / ADR 0033). The label is the contract's NAME; the billing method is
 * the chip beside it. Mapping is explicit — "No contract" is a real state
 * (hours only, D7), not an inheritance.
 *
 * Since 3 Nov (Mette) it is the value of the panel's Contract row rather than
 * the full-width pill under its own "Map task to contract" header: the letter
 * badge and the name, as Contract insights and the plan row show the
 * contract, and a chevron; a light ground on hover says it is a control. An
 * unmapped task shows the row's "+ Add contract" — so a task reads unpriced
 * the same way in the table and here. The billing method and factor stay in
 * Contract insights, right below.
 *
 * The selection rule itself lives in `useContractSelection`, shared with the
 * plan row's `RowContractChip`. This component is the panel's trigger and
 * nothing more.
 */
export const ContractPicker = ({ node }: { node: PlanNode }) => {
  const {
    anchor,
    contract,
    contractId,
    contractInputs,
    options,
    open,
    close,
    choose,
    remapDialog,
  } = useContractSelection(node);
  const { t } = useTranslation(['pmPlanningPanel', 'pmPlanning']);
  const noContractLabel = t('contractPicker.NoContract');

  if (contractInputs.length === 0) {
    return (
      <Box component="span" sx={{ color: 'var(--fg-3)' }} data-automation-id="detail-contract-none">
        {noContractLabel}
      </Box>
    );
  }

  const buttonLabel = contract ? contract.name : t('pmPlanning:contract.AddContract');

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={t('contractPicker.ChangeContract')}
        data-automation-id="detail-contract-button"
        data-mapped={contract !== undefined}
        onClick={(event) => open(event.currentTarget as HTMLElement)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
          // Padded so the hover ground has room; pulled back by the same on
          // the right, so the chevron lines up with the other rows' values.
          py: 0.5,
          pl: 0.5,
          pr: 0.25,
          mr: -0.25,
          border: 'none',
          bgcolor: 'transparent',
          fontSize: 11,
          fontFamily: 'var(--font-family)',
          color: contract ? 'var(--fg-1)' : 'var(--fg-3)',
          cursor: 'pointer',
          borderRadius: 'var(--border-radius-small)',
          transition: 'background-color var(--duration-instant)',
          '&:hover': { bgcolor: 'var(--bg-sunken)' },
          '&:hover .contract-picker-name': { color: 'var(--color-blue-80)' },
          '&:hover .contract-picker-add': {
            borderColor: 'var(--color-blue-80)',
            color: 'var(--color-blue-80)',
          },
          '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '1px' },
        }}
      >
        {contract ? (
          <ContractChip type={contract.type} size={16} />
        ) : (
          // The plan row's unmapped square (`RowContractChip`).
          <Box
            component="span"
            className="contract-picker-add"
            sx={{
              width: 16,
              height: 16,
              borderRadius: '4px',
              border: 'var(--border-width-thin) dashed var(--border-subtle)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              color: 'var(--fg-3)',
              transition: 'color var(--duration-instant), border-color var(--duration-instant)',
            }}
          >
            <MaterialSymbol name="add" size={12} />
          </Box>
        )}
        <Box
          component="span"
          className="contract-picker-name"
          sx={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {buttonLabel}
        </Box>
        <MaterialSymbol name="expand_more" size={16} color="var(--fg-3)" />
      </Box>
      <Menu
        open={anchor !== null}
        anchorEl={anchor}
        onClose={close}
        // Right-aligned under the plain-text trigger, which is narrower than
        // the options' names.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { minWidth: 220, borderRadius: 'var(--border-radius-medium)' } },
        }}
      >
        {options.map((option) => {
          const label = option.kind === 'none' ? noContractLabel : option.label;
          return (
            <MenuItem
              key={option.contractId ?? 'none'}
              selected={option.contractId === contractId}
              data-automation-id="contract-option"
              data-kind={option.kind}
              onClick={() => choose(option.contractId, label)}
              sx={{ gap: '9px', px: 1.5, py: '7px', fontSize: 14 }}
            >
              {option.type ? (
                <ContractChip type={option.type} size={18} />
              ) : (
                <Box component="span" sx={{ width: 18, height: 18, flex: 'none' }} />
              )}
              {label}
            </MenuItem>
          );
        })}
      </Menu>
      {remapDialog}
    </>
  );
};
